// Feb 2026 -- production email verification landing page.
// Consumes the ?token= that Resend delivered and reports success,
// expired-link, or a generic error. Only active in deployments where
// REQUIRE_EMAIL_VERIFICATION=true; behaviour is otherwise inert.

import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  // states: verifying | verified | expired | error
  const [state, setState] = useState("verifying");
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);

  useEffect(() => {
    (async () => {
      if (!token) { setState("error"); return; }
      try {
        await api.post("/auth/verify-email", { token });
        setState("verified");
      } catch (err) {
        const detail = err?.response?.data?.detail;
        const msg = typeof detail === "string" ? detail : formatError(detail);
        if (/expired|invalid/i.test(msg || "")) setState("expired");
        else setState("error");
      }
    })();
  }, [token]);

  const resend = async () => {
    if (!resendEmail) { toast.error("Please enter your email."); return; }
    setResending(true);
    try {
      await api.post("/auth/send-verification-email", { email: resendEmail });
      toast.success("If an account exists for that email, we have sent a new verification link.");
    } catch {
      toast.success("If an account exists for that email, we have sent a new verification link.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#F6F9FE] px-4">
      <div className="w-full max-w-md bg-white rounded-xl border border-slate-200 p-8 shadow-sm"
           data-testid="verify-email-page">
        {state === "verifying" && (
          <>
            <h1 className="font-display text-2xl text-[#0B1A36] mb-3"
                data-testid="verify-email-verifying-heading">
              Verifying your email
            </h1>
            <p className="text-sm text-[#4B5773]">Hang on a moment.</p>
          </>
        )}
        {state === "verified" && (
          <>
            <h1 className="font-display text-2xl text-[#0B1A36] mb-3"
                data-testid="verify-email-success-heading">
              Email verified. You are all set.
            </h1>
            <p className="text-sm text-[#4B5773] mb-6">
              Thanks for confirming your email. You can now continue using NextRota.
            </p>
            <Link to="/app" data-testid="verify-email-continue-link">
              <Button className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-lg"
                      data-testid="verify-email-continue-btn">
                Continue to NextRota
              </Button>
            </Link>
          </>
        )}
        {state === "expired" && (
          <>
            <h1 className="font-display text-2xl text-[#0B1A36] mb-3"
                data-testid="verify-email-expired-heading">
              Link expired
            </h1>
            <p className="text-sm text-[#4B5773] mb-4">
              This verification link has expired. We can send you a new one.
            </p>
            <input type="email" required value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-3"
                    data-testid="verify-email-resend-email-input" />
            <Button onClick={resend} disabled={resending}
                    className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-lg"
                    data-testid="verify-email-send-new-link-btn">
              {resending ? "Sending..." : "Send a new link"}
            </Button>
          </>
        )}
        {state === "error" && (
          <>
            <h1 className="font-display text-2xl text-[#0B1A36] mb-3"
                data-testid="verify-email-error-heading">
              Verification link could not be used
            </h1>
            <p className="text-sm text-[#4B5773] mb-4">
              This verification link is invalid or has already been used.
              You can request a new one below.
            </p>
            <input type="email" required value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-3"
                    data-testid="verify-email-error-resend-email-input" />
            <Button onClick={resend} disabled={resending}
                    className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-lg"
                    data-testid="verify-email-error-send-new-link-btn">
              {resending ? "Sending..." : "Send a new link"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
